import { RefObject, useCallback } from 'react';

import { getViewportForBounds, InternalNode, useReactFlow, useStoreApi } from '@xyflow/react';
import { toPng } from 'html-to-image';

export const useGraphDownload = (
  containerRef: RefObject<HTMLDivElement>,
  controlsRef: RefObject<HTMLDivElement>,
  filename = 'graph.png'
) => {
  const { getViewport, setViewport } = useReactFlow();
  const storeApi = useStoreApi();

  return useCallback(async () => {
    if (!containerRef.current) return;

    const { panZoom, nodeLookup, domNode } = storeApi.getState();
    if (!panZoom || !nodeLookup || !domNode) return;

    const previousViewport = getViewport();

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    nodeLookup.forEach((node: InternalNode) => {
      const { x, y } = node.internals.positionAbsolute;
      const { width = 0, height = 0 } = node.measured ?? {};
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    if (minX === Infinity) return;

    const bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    const viewport = getViewportForBounds(bounds, domNode.offsetWidth, domNode.offsetHeight, 0.1, 2, 0.12);
    panZoom.setViewport(viewport, { duration: 0 });

    // Wait two frames for React Flow to apply the new transform before capturing.
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    try {
      if (controlsRef.current) controlsRef.current.style.visibility = 'hidden';

      const dataUrl = await toPng(containerRef.current, {
        cacheBust: true,
        pixelRatio: window.devicePixelRatio * 2,
      });

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } finally {
      if (controlsRef.current) controlsRef.current.style.visibility = '';
      setViewport(previousViewport, { duration: 300 });
    }
  }, [storeApi, getViewport, setViewport, containerRef, controlsRef, filename]);
};
